import React, { useMemo } from 'react';
import type { GetConnectionAndMappingResponse } from '@api/features/accounting-system/getConnectionAndMapping';
import type { GetSuppliersResponse } from '@api/features/suppliers/getSuppliers';
import { BrokersReturnType } from '@api/features/brokers/getBrokers';
import EditableTable from '@client/components/EditableTable/EditableTable';
import Card from '@client/components/Card/Card';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	createMappingEntry,
	getAccountingSuppliers,
	getBrokers,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';

const BrokerMappingTab = ({
	connection,
	refreshConnection,
}: {
	connection: GetConnectionAndMappingResponse | undefined;
	refreshConnection: () => void;
}) => {
	const [brokers, refreshbrokers] = useFetchedState(getBrokers);
	const [vendors] = useFetchedState(async () => (
		connection == null ? [] : getAccountingSuppliers(connection.id)
	), [connection]);

	const onSave = async (
		brokerId: number | undefined,
		update: Partial<GetSuppliersResponse[number] & { accountingCode: string }>,
	) => {
		const { accountingCode } = update;

		if (accountingCode != null && brokerId != null && connection != null) {
			try {
				await createMappingEntry('broker', brokerId, accountingCode, connection.id);
				await refreshConnection();
				showSuccessNotification('Successfully mapped entry');
			} catch (e) {
				showErrorNotification('Could not create mapping', e as Error);
			}
		}

		refreshbrokers();
	};

	const brokersList = useMemo(() => {
		if (
			brokers == null ||
            connection == null
		) {
			return [];
		}

		const formatted = brokers.map((s) => {
			const mappingEntry = connection.AccountingMappingEntries.find((mapping) => (
				mapping.type === 'broker' &&
                mapping.cvIdentifier === String(s.id)
			));

			return {
				...s,
				accountingCode: mappingEntry?.code ?? undefined,
			};
		});

		return formatted;
	}, [brokers, connection]);

	return (
		<Card slim>
			<EditableTable<BrokersReturnType & { id: number; accountingCode: string | undefined }, 'id'>
				actionsTitle=""
				iconButtons
				pagination={false}
				keyDataIndex="id"
				onSave={onSave}
				dataSource={brokersList}
				columns={[
					{
						dataIndex: 'id',
						title: 'ID',
					},
					{
						dataIndex: 'name',
						title: 'Name',
					},
					{
						dataIndex: 'accountingCode',
						title: 'Accounting System Entry',
						editingProps: {
							type: 'select',
							options: (vendors ?? []).map((c) => ({
								label: c.name,
								value: c.id,
							})),
							inputProps: {
								placeholder: 'Select broker',
								showSearch: true,
							},
						},
						editable: true,
						render: (v: string | null) => (
							v == null ? (<i>Empty</i>) : vendors?.find((c) => c.id === v)?.name
						),
					},
				]}
			/>
		</Card>
	);
};

export default BrokerMappingTab;
